import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

const FALLBACK_TOKEN_KEY = 'sb_access_token_fallback';

/**
 * Last-resort token read: Supabase JS persists the session under
 * `sb-<ref>-auth-token`. If `supabase.auth.getSession()` throws because of a
 * Navigator LockManager contention (common when a user has multiple tabs
 * open), we parse that storage entry directly so API calls still ship a
 * valid Bearer token instead of silently going out unauthenticated.
 */
const _readPersistedSupabaseToken = () => {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i) || '';
      if (!(key.startsWith('sb-') && key.endsWith('-auth-token'))) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.access_token) return parsed.access_token;
    }
  } catch {
    /* malformed storage — ignore */
  }
  return null;
};

/**
 * Get the current access token.
 * When impersonating, always use the impersonation token from localStorage.
 * Otherwise, prefer the live Supabase session; if that throws (lock contention
 * across tabs is the common cause), fall back to the fallback token key, then
 * to the persisted Supabase session on disk.
 */
export const getAccessToken = async () => {
  if (localStorage.getItem('gospeleon_impersonating')) {
    return localStorage.getItem(FALLBACK_TOKEN_KEY);
  }
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      // Keep the fallback mirrored so a lock contention next time still works.
      try { localStorage.setItem(FALLBACK_TOKEN_KEY, session.access_token); } catch { /* quota */ }
      return session.access_token;
    }
  } catch {
    /* NavigatorLockManager contention or malformed storage — fall through */
  }
  const fallback = localStorage.getItem(FALLBACK_TOKEN_KEY);
  if (fallback) return fallback;
  return _readPersistedSupabaseToken();
};

/**
 * Set up axios to automatically include the Supabase Bearer token.
 * The interceptor must never throw — if token resolution fails, the request
 * still fires (the backend will return 401 and the caller handles that), but
 * we don't want a lock error to make every API call reject before it hits
 * the network.
 */
export const setupAxiosAuth = (axios) => {
  axios.defaults.withCredentials = true;
  axios.interceptors.request.use(async (config) => {
    try {
      const token = await getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch {
      /* never block the request on auth resolution */
    }
    return config;
  });
};

// Mirror the Supabase session token into our fallback key on every auth
// state change (sign-in, token refresh, sign-out). That way, if the Navigator
// LockManager jams up a subsequent `getSession()` call, we still have the
// latest access_token on disk to use.
//
// IMPORTANT: skip the mirror while impersonating. The fallback key holds the
// impersonation JWT during an admin diagnostic session; overwriting it with
// the admin's own Supabase session (which is still persisted in storage) is
// what caused the "nothing happens after Switched to <user>" bug.
supabase.auth.onAuthStateChange((_event, session) => {
  try {
    if (localStorage.getItem('gospeleon_impersonating')) return;
    if (session?.access_token) {
      localStorage.setItem(FALLBACK_TOKEN_KEY, session.access_token);
    } else if (_event === 'SIGNED_OUT') {
      localStorage.removeItem(FALLBACK_TOKEN_KEY);
    }
  } catch { /* quota or sandboxed storage */ }
});

// Swallow Supabase's internal NavigatorLockManager auto-refresh rejections so
// they never surface as "Uncaught (in promise)" errors in the user's console
// or trip Sentry/window error handlers. The rejection is harmless (the next
// tick retries on its own); it only noises up the browser DevTools.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const msg = String(event?.reason?.message || event?.reason || '');
    if (/navigator ?locks|LockManager|auth-token.*lock/i.test(msg)) {
      event.preventDefault();
    }
  });
}
